import { css } from 'aphrodite';
import * as React from 'react';
import { useRouteMatch } from 'react-router';
import { BrokerDealer, Industry, TemplateCategoryType } from '../../../../extViewmodels';
import { Checkbox } from '../../../../web/components/Checkbox';
import { Modal } from '../../../../web/components/Modal';
import { ISelectBoxOption, SelectBox } from '../../../../web/components/SelectBox';
import { TextInput } from '../../../../web/components/TextInput';
import { baseStyleSheet as bs } from '../../../../web/styles/styles';
import { getBrokerDealerOptions } from '../../../containers/account/getBrokerDealerOptions';
import { styleSheet } from './styles';

export function NewCategoryModal({
	isOpen,
	onCancel,
	onSave,
}: {
	isOpen: boolean;
	onCancel: () => void;
	onSave: (name: string, type: TemplateCategoryType) => void;
}) {
	const [categoryName, setCategoryName] = React.useState<string>('');
	const [createCategoryForBD, setCreateCategoryForBD] = React.useState<boolean>(false);
	const [selectedBrokerDealer, setSelectedBrokerDealer] = React.useState<BrokerDealer | null>(null);
	const match = useRouteMatch<{ category: string }>('/campaigns/:category');

	const industryFromUrl = React.useMemo(() => {
		const category = match.params.category || '';
		const matchedIndustry = Object.values(Industry).find(ind => ind.toLowerCase() === category.toLowerCase());
		return matchedIndustry;
	}, [match.params]);

	const [industry] = React.useState<Industry>(industryFromUrl);
	const brokerDealerOptions = React.useMemo(() => getBrokerDealerOptions(industry), [industry]);
	const hasBrokerDealers = !!brokerDealerOptions;

	const onBrokerDealerChanged = (option: ISelectBoxOption<BrokerDealer>) => {
		setSelectedBrokerDealer(option.value);
		setCategoryName(option.value);
	};

	const getBrokerDealerSelection = (): ISelectBoxOption<BrokerDealer> | null => {
		if (!selectedBrokerDealer) {
			return null;
		}
		return {
			title: selectedBrokerDealer,
			value: selectedBrokerDealer,
		};
	};

	const handleClose = () => {
		setCategoryName('');
		setCreateCategoryForBD(false);
		setSelectedBrokerDealer(null);
		onCancel();
	};

	const handleSave = () => {
		const categoryType = createCategoryForBD ? TemplateCategoryType.BrokerDealer : TemplateCategoryType.Default;
		onSave(categoryName, categoryType);
		setCategoryName('');
		setCreateCategoryForBD(false);
		setSelectedBrokerDealer(null);
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={onCancel}>
			<div className={css(styleSheet.modalTitle)}>Add New Category</div>
			<div className={css(styleSheet.inputTitle)}>Category Name</div>

			<TextInput
				inputId='new-category-input'
				onChange={ev => setCategoryName(ev.target.value)}
				type='text'
				value={categoryName}
				disabled={createCategoryForBD}
			/>

			{hasBrokerDealers && (
				<div className={css(bs.mt3)}>
					<Checkbox
						checked={createCategoryForBD}
						onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setCreateCategoryForBD(ev.target.checked)}
						label='Create Category for Broker Dealer'
					>
						<span>Create Category for Broker Dealer</span>
					</Checkbox>
				</div>
			)}

			{hasBrokerDealers && createCategoryForBD && (
				<div className={css(bs.mt3)}>
					<div className={css(bs.textTitles, bs.textSm, bs.mb1)}>Broker Dealer</div>
					<SelectBox
						contentClassName={`${css(styleSheet.dropdownMenu)}`}
						onSelectionChanged={onBrokerDealerChanged}
						options={brokerDealerOptions || []}
						selectedOption={getBrokerDealerSelection()}
						triggerClassName={`${css(styleSheet.dropdownTrigger)}`}
					/>
				</div>
			)}

			<div className={css(bs.mt5, bs.flex, bs.itemsCenter, bs.gap5)}>
				<button className={css(bs.ctaButton)} onClick={handleSave}>
					<span>Create</span>
				</button>
				<button className={css(bs.ctaButtonReverse)} onClick={handleClose}>
					<span>Cancel</span>
				</button>
			</div>
		</Modal>
	);
}
